import { immerable } from 'immer';
import type Stripe from 'stripe';
import type { PodcastDTO } from '../dto/podcast.dto.js';
import type { StripeAccountDTO, StripeCustomerDTO, StripeSubscriptionDTO } from '../dto/stripe.dto.js';
import type { UserDTO } from '../dto/user.dto.js';
import { isObjectIdStringified, TEST_USER_EMAIL } from '../utils/index.js';
import { NonPopulatedError } from './errors.model.js';
import { Podcast, type PodcastEpisode } from './podcast.model.js';
import type { Tier } from './tier.model.js';

export class User implements UserDTO {
  [immerable] = true;

  _id: string;
  deletionRequestDate?: string;
  email: string;
  invitationSetup: UserDTO['invitationSetup'];
  ownedChannels: Podcast[];
  pseudo?: string;
  settings: UserDTO['settings'];
  stripeAccount?: StripeAccountDTO;
  stripeCustomer?: StripeCustomerDTO;
  subscribedChannels: (string | Podcast)[];

  constructor(data: UserDTO) {
    if (data.ownedChannels.some(c => typeof c === 'string')) throw new NonPopulatedError('user.ownedChannels');
    this._id = data._id;
    this.deletionRequestDate = data.deletionRequestDate;
    this.email = data.email;
    this.invitationSetup = data.invitationSetup;
    this.ownedChannels = data.ownedChannels.map(c => new Podcast(c as PodcastDTO));
    this.pseudo = data.pseudo;
    this.settings = data.settings;
    this.stripeAccount = data.stripeAccount;
    this.stripeCustomer = data.stripeCustomer;
    this.subscribedChannels = data.subscribedChannels.map(c =>
      isObjectIdStringified(c) ? c.toString() : new Podcast(c),
    );
  }

  doesPodcastRequiresVerificationForInvitedUser(podcastId: string) {
    return this.invitationSetup?.channelsPendingVerification?.includes(podcastId);
  }

  ownsAPodcast() {
    return this.ownedChannels.length > 0;
  }

  isInternalUser() {
    return this.email.endsWith('@fuuze.app');
  }

  isCreator(channelId?: string): boolean {
    if (!channelId) return false;
    const doesUserOwnsChannel = !!this.ownedChannels.find(c => c._id === channelId);
    if (doesUserOwnsChannel) {
      // User owns the channel, so he is creator.
      // We still need to verify he is not an invited user and this channel is pending verification
      if (this.invitationSetup?.channelsPendingVerification) {
        const isPendingVerification = !!this.invitationSetup.channelsPendingVerification.find(c => c === channelId);
        return doesUserOwnsChannel && !isPendingVerification;
      }
      return doesUserOwnsChannel;
    }
    return (
      !!this.ownedChannels.find(c => c._id === channelId) &&
      !!this.invitationSetup?.channelsPendingVerification?.every(c => c !== channelId)
    );
  }

  isFinancialSupporter(channelId?: string) {
    if (!channelId) return false;
    const subscription = this.getSubscriptionForChannel(channelId);
    return subscription?.status === 'active';
  }

  getSubscriptionForChannel(channelId: string) {
    return this.stripeCustomer?.subscriptions?.data.find(
      s => (s.items.data[0]?.price.product as Stripe.Product).metadata.channelId === channelId,
    );
  }

  getSubscriptionForTier(tier: Tier) {
    return this.stripeCustomer?.subscriptions?.data.find(s => s.items.data[0]?.price.id === tier.stripePrice.id);
  }

  getOwnedChannel(channelId: string) {
    return this.ownedChannels.find(c => c._id === channelId);
  }

  doesSubscriptionHasIssue(subscription: StripeSubscriptionDTO): boolean {
    return (
      subscription.status === 'incomplete' ||
      subscription.status === 'incomplete_expired' ||
      !!subscription.pending_update ||
      (subscription.status === 'past_due' && this.stripeCustomer?.payment_methods.length === 0)
    );
  }

  isAnySubscriptionHasIssue() {
    return this.stripeCustomer?.subscriptions?.data.some(s => this.doesSubscriptionHasIssue(s));
  }

  isSomeStripeRequirementsEventuallyDue() {
    return (this.stripeAccount?.requirements?.eventually_due?.length || 0) > 0;
  }

  isStripeOnboardingNeeded() {
    return (
      !this.stripeAccount?.details_submitted || (this.stripeAccount?.requirements?.eventually_due?.length || 0) > 0
    );
  }

  hasPaymentMethods() {
    return (this.stripeCustomer?.payment_methods.length || 0) > 0;
  }

  hasOngoingSubscriptionsButNoPaymentMethods() {
    const hasSubscriptions = (this.stripeCustomer?.subscriptions?.data.length || 0) > 0;
    return hasSubscriptions && !this.hasPaymentMethods();
  }

  isE2eTestingAccount() {
    return this.email === TEST_USER_EMAIL;
  }

  canListenToPodcast(params: { episode: PodcastEpisode; podcast: Podcast }) {
    return canUserListenToPodcast({ ...params, user: this });
  }
}

export function canUserListenToPodcast(params: { episode: PodcastEpisode; podcast: Podcast; user?: User }) {
  const { episode, podcast, user } = params;

  if (episode.isFakeForLoading()) return false;
  if (episode.isVisibility('public')) return true;
  if (user?.isCreator(episode.channel)) return true;

  const subscription = user?.getSubscriptionForChannel(podcast._id);
  if (subscription) {
    if (user?.doesSubscriptionHasIssue(subscription)) return false;

    const tierIdSubscribed = (subscription.items.data[0]?.price?.product as Stripe.Product).metadata.tierId;
    if (!tierIdSubscribed) throw new Error('No tierId in subscription');

    const tierSubscribed = podcast.getTier(tierIdSubscribed);
    if (!tierSubscribed) throw new Error('No tier found');

    const amountOfTierOfEpisode = podcast.getTier(episode.visibility)?.amount;
    if (amountOfTierOfEpisode === undefined) throw new Error('Tier is undefined');
    if (tierSubscribed.amount >= amountOfTierOfEpisode) return true;
  }

  return false;
}
